import React, { useState, useMemo } from 'react';
import { Button, Modal, Form, Input, message, Popconfirm, Checkbox, Row, Col } from 'antd';
import { DataTable, PageLayout } from '../../ui';
import { useGetRolesListQuery, useCreateRoleMutation, useUpdateRoleMutation, useDeleteRoleMutation } from '../../store/api/usersApi';
import type { Role } from '../../types';
import useHasPermission from '../../hooks/useHasPermission';
import { PERMISSION_GROUPS, ALL_PERMISSIONS } from '../../constants/permissionCatalog';

const RolesPage: React.FC = () => {
  const { data: roles = [], isLoading } = useGetRolesListQuery();
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();
  const [deleteRole] = useDeleteRoleMutation();
  const canEditRoles = useHasPermission('roles.edit');
  const canDeleteRoles = useHasPermission('roles.delete');
  const canCreateRoles = useHasPermission('roles.create');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [form] = Form.useForm();
  const [checkedPermissions, setCheckedPermissions] = useState<Record<string, boolean>>({});

  const openEdit = (role: Role) => {
    setEditingRole(role);
    form.setFieldsValue({ 
      name: role.name,
      description: role.description || ''
    });
    const perms = role.permissions || {};
    const map: Record<string, boolean> = {};
    ALL_PERMISSIONS.forEach(p => map[p.key] = Boolean(perms[p.key]));
    setCheckedPermissions(map);
    setIsModalVisible(true);
  };

  const openCreate = () => {
    setEditingRole(null);
    form.resetFields();
    form.setFieldsValue({ description: '' });
    const empty: Record<string, boolean> = {};
    ALL_PERMISSIONS.forEach(p => empty[p.key] = false);
    setCheckedPermissions(empty);
    setIsModalVisible(true);
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const perms = { ...checkedPermissions };
      if (editingRole) {
        if (!canEditRoles) throw new Error('Нет прав');
        await updateRole({ 
          id: editingRole.id, 
          body: { 
            name: values.name, 
            description: values.description || '',
            permissions: perms 
          } 
        }).unwrap();
        message.success('Изменения роли сохранены');
      } else {
        if (!canCreateRoles) throw new Error('Нет прав');
        await createRole({ 
          name: values.name, 
          description: values.description || '', 
          permissions: perms 
        } as any).unwrap();
        message.success('Роль создана');
      }
      setIsModalVisible(false);
    } catch (e: any) {
      console.error(e);
      message.error(e?.data?.error || 'Ошибка');
    }
  };

  const handleDelete = async (role: Role) => {
    try {
      if (!canDeleteRoles) throw new Error('Нет прав');
      await deleteRole(role.id).unwrap();
      message.success('Роль удалена');
    } catch (e: any) {
      console.error(e);
      message.error(e?.data?.error || 'Ошибка');
    }
  };

  const onPermissionChange = (perm: string, checked: boolean) => {
    setCheckedPermissions(prev => ({ ...prev, [perm]: checked }));
  };

  const permissionsGrid = useMemo(() => (
    <div style={{ maxHeight: 420, overflow: 'auto' }}>
      {PERMISSION_GROUPS.map(group => (
        <div key={group.title} style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px dashed var(--card-border)' }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>{group.title}</div>
          <Row gutter={[8, 8]}>
            {group.permissions.map(p => (
              <Col span={8} key={p.key}>
                <Checkbox checked={!!checkedPermissions[p.key]} onChange={(e) => onPermissionChange(p.key, e.target.checked)}>{p.label}</Checkbox>
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  ), [checkedPermissions]);

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Описание', dataIndex: 'description', key: 'description' },
    { title: 'Действия', key: 'actions', render: (_: any, record: Role) => (
      <>
        {canEditRoles && <Button style={{ marginRight: 8 }} onClick={() => openEdit(record)}>Редактировать</Button>}
        {canDeleteRoles && (
          <Popconfirm title="Удалить роль?" onConfirm={() => handleDelete(record)} okText="Да" cancelText="Нет">
            <Button danger>Удалить</Button>
          </Popconfirm>
        )}
      </>
    ) }
  ];

  return (
    <PageLayout
      title="Роли"
      subtitle="Матрица прав доступа пользователей"
      actions={
        canCreateRoles ? (
          <Button type="primary" onClick={openCreate}>
            Создать роль
          </Button>
        ) : undefined
      }
    >
      <DataTable rowKey="id" loading={isLoading} dataSource={roles} columns={columns} />

      <Modal open={isModalVisible} title={editingRole ? 'Редактирование роли' : 'Создание роли'} onCancel={() => setIsModalVisible(false)} onOk={handleOk} width={800}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название роли' }]}>
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={2} placeholder="Описание роли" />
          </Form.Item>

          <Form.Item label="Права доступа">
            {permissionsGrid}
          </Form.Item>

        </Form>
      </Modal>
    </PageLayout>
  );
};

export default RolesPage;


